import { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  MoreVertical,
  Trash2,
  Edit2,
  X,
  Plus,
  Bookmark,
  BookmarkCheck,
  FileText,
  Loader2,
  FolderOpen,
  FolderPlus,
  Settings2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { collectionsApi } from '@/services/api';

interface Collection {
  collection_id: string;
  name: string;
  created_date: string;
  resource_ids: string[];
}

interface ActiveDocument {
  id: string;
  title: string;
  type: string;
  status: string;
  resource_id?: string;
  file_type?: string;
  file_name?: string;
  subtitle?: string;
}

interface CollectionsManagerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, enables the "Active Documents" tab. */
  activeDocuments?: ActiveDocument[];
  /** Called to remove a document from the active session. */
  onRemoveActiveDocument?: (identifier: string, docType: string) => void;
  /** Called when "Add Documents" is clicked. Closes the modal first. */
  onAddDocuments?: () => void;
  /** Default tab to show when the modal opens ("active" or "collections"). */
  defaultTab?: 'active' | 'collections';
}

/** Format a raw resource_id into a human-readable label. */
function formatResourceId(resourceId: string): string {
  if (!resourceId) return resourceId;

  if (resourceId.startsWith('patent-')) {
    const num = resourceId.replace('patent-', '');
    return `US Patent ${Number(num).toLocaleString('en-US')}`;
  }
  if (resourceId.startsWith('publication-')) {
    const num = resourceId.replace('publication-', '');
    return `US Pub ${num}`;
  }
  if (resourceId.startsWith('application-')) {
    const num = resourceId.replace('application-', '');
    return `Application ${num}`;
  }
  if (resourceId.startsWith('foreign_patent-')) {
    return resourceId.replace('foreign_patent-', '');
  }

  // Uploads / other — just show as-is (may be a filename)
  return resourceId;
}

export function CollectionsManagerModal({
  open,
  onOpenChange,
  activeDocuments,
  onRemoveActiveDocument,
  onAddDocuments,
  defaultTab = 'active',
}: CollectionsManagerModalProps) {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [newCollectionName, setNewCollectionName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Active docs tab state
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [showBulkCollectionPicker, setShowBulkCollectionPicker] = useState(false);

  const hasActiveTab = !!activeDocuments;

  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const data = await collectionsApi.list();
      setCollections(data);
    } catch (err) {
      console.error('Failed to load collections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load collections when dialog opens & reset selection
  useEffect(() => {
    if (open) {
      loadCollections();
      setSelectedDocIds(new Set());
      setShowBulkCollectionPicker(false);
    }
  }, [open, loadCollections]);

  // Listen for external updates while open
  useEffect(() => {
    if (!open) return;
    const handler = () => loadCollections();
    window.addEventListener('collections-updated', handler);
    return () => window.removeEventListener('collections-updated', handler);
  }, [open, loadCollections]);

  const dispatchUpdate = () => {
    window.dispatchEvent(new Event('collections-updated'));
  };

  // ── Collection CRUD ────────────────────────────────────────────────────────

  const handleCreate = async () => {
    const name = newCollectionName.trim();
    if (!name) return;
    try {
      const collectionId = crypto.randomUUID();
      await collectionsApi.create(collectionId, name);
      setCollections(prev => [
        ...prev,
        { collection_id: collectionId, name, created_date: new Date().toISOString(), resource_ids: [] },
      ]);
      setNewCollectionName('');
      dispatchUpdate();
      return collectionId;
    } catch (err) {
      console.error('Failed to create collection:', err);
    }
  };

  const handleRename = async (collectionId: string) => {
    const name = renameValue.trim();
    if (!name) return;
    try {
      await collectionsApi.rename(collectionId, name);
      setCollections(prev =>
        prev.map(c => (c.collection_id === collectionId ? { ...c, name } : c)),
      );
      setRenamingId(null);
      setRenameValue('');
      dispatchUpdate();
    } catch (err) {
      console.error('Failed to rename collection:', err);
    }
  };

  const handleDelete = async (collectionId: string) => {
    try {
      await collectionsApi.delete(collectionId);
      setCollections(prev => prev.filter(c => c.collection_id !== collectionId));
      setDeleteConfirmId(null);
      dispatchUpdate();
    } catch (err) {
      console.error('Failed to delete collection:', err);
    }
  };

  const handleRemoveDoc = async (collectionId: string, resourceId: string) => {
    try {
      await collectionsApi.removeDoc(collectionId, resourceId);
      setCollections(prev =>
        prev.map(c =>
          c.collection_id === collectionId
            ? { ...c, resource_ids: c.resource_ids.filter(id => id !== resourceId) }
            : c,
        ),
      );
      dispatchUpdate();
    } catch (err) {
      console.error('Failed to remove doc from collection:', err);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startRename = (col: Collection) => {
    setRenamingId(col.collection_id);
    setRenameValue(col.name);
  };

  const isInAnyCollection = (resourceId: string) =>
    collections.some(c => c.resource_ids.includes(resourceId));

  // ── Bulk add helpers ───────────────────────────────────────────────────────

  const toggleDocInCollection = async (collectionId: string, resourceId: string) => {
    const col = collections.find(c => c.collection_id === collectionId);
    if (!col) return;
    const isIn = col.resource_ids.includes(resourceId);
    if (isIn) {
      await collectionsApi.removeDoc(collectionId, resourceId);
      setCollections(prev =>
        prev.map(c =>
          c.collection_id === collectionId
            ? { ...c, resource_ids: c.resource_ids.filter(id => id !== resourceId) }
            : c,
        ),
      );
    } else {
      await collectionsApi.addDoc(collectionId, resourceId);
      setCollections(prev =>
        prev.map(c =>
          c.collection_id === collectionId
            ? { ...c, resource_ids: [...c.resource_ids, resourceId] }
            : c,
        ),
      );
    }
    dispatchUpdate();
  };

  const addMultipleDocsToCollection = async (collectionId: string, resourceIds: string[]) => {
    for (const rid of resourceIds) {
      const col = collections.find(c => c.collection_id === collectionId);
      if (col && !col.resource_ids.includes(rid)) {
        await collectionsApi.addDoc(collectionId, rid);
      }
    }
    const updated = await collectionsApi.list();
    setCollections(updated);
    dispatchUpdate();
  };

  // ── Collections tab content ────────────────────────────────────────────────

  const collectionsContent = (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : collections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <FolderOpen className="w-10 h-10 mb-2 text-gray-300" />
            <p className="text-sm">No collections yet</p>
            <p className="text-xs text-gray-400 mt-1">Create one below to get started</p>
          </div>
        ) : (
          <div className="space-y-1">
            {collections.map(col => {
              const isExpanded = expandedIds.has(col.collection_id);
              const isRenaming = renamingId === col.collection_id;

              return (
                <div key={col.collection_id} className="border border-gray-200 rounded-lg">
                  {/* Collection header row */}
                  <div
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-t-lg"
                    onClick={() => toggleExpanded(col.collection_id)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}

                    {isRenaming ? (
                      <Input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRename(col.collection_id);
                          if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                        }}
                        onBlur={() => handleRename(col.collection_id)}
                        onClick={e => e.stopPropagation()}
                        className="h-7 text-sm flex-1"
                      />
                    ) : (
                      <span className="text-sm font-medium flex-1 truncate">{col.name}</span>
                    )}

                    <Badge variant="secondary" className="text-xs flex-shrink-0">
                      {col.resource_ids.length} doc{col.resource_ids.length !== 1 ? 's' : ''}
                    </Badge>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 flex-shrink-0"
                          onClick={e => e.stopPropagation()}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                        <DropdownMenuItem
                          onClick={e => { e.stopPropagation(); startRename(col); }}
                          className="cursor-pointer"
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={e => { e.stopPropagation(); setDeleteConfirmId(col.collection_id); }}
                          className="text-red-600 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Expanded document list */}
                  {isExpanded && (
                    <div className="border-t border-gray-100">
                      {col.resource_ids.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-gray-400 italic">
                          No documents in this collection
                        </div>
                      ) : (
                        col.resource_ids.map(rid => (
                          <div
                            key={rid}
                            className="flex items-center gap-2 px-4 py-1.5 hover:bg-gray-50 group"
                          >
                            <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-700 flex-1 truncate" title={rid}>
                              {formatResourceId(rid)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                              onClick={() => handleRemoveDoc(col.collection_id, rid)}
                              title="Remove from collection"
                            >
                              <X className="w-3.5 h-3.5 text-gray-500 hover:text-red-600" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create new collection */}
      <div className="flex items-center gap-2 pt-2 border-t flex-shrink-0">
        <Input
          placeholder="New collection name..."
          value={newCollectionName}
          onChange={e => setNewCollectionName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleCreate();
          }}
        />
        <Button
          onClick={handleCreate}
          disabled={!newCollectionName.trim()}
          className="gap-1"
        >
          <Plus className="w-4 h-4" />
          Create
        </Button>
      </div>
    </>
  );

  // ── Active documents tab content ───────────────────────────────────────────

  const docsWithResourceId = activeDocuments?.filter(d => d.resource_id) ?? [];

  const activeDocsContent = activeDocuments ? (
    <>
      {/* Select All row */}
      <div className="flex items-center gap-2 pb-2 border-b flex-shrink-0">
        <Checkbox
          checked={selectedDocIds.size === docsWithResourceId.length && selectedDocIds.size > 0}
          onCheckedChange={(checked) => {
            if (checked) {
              setSelectedDocIds(new Set(docsWithResourceId.map(d => d.resource_id!)));
            } else {
              setSelectedDocIds(new Set());
            }
          }}
        />
        <span className="text-sm font-medium">Select All</span>
        <span className="text-xs text-gray-500 ml-auto">
          {activeDocuments.length} document{activeDocuments.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Document list */}
      <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
        {activeDocuments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <FileText className="w-10 h-10 mb-2 text-gray-300" />
            <p className="text-sm">No active documents</p>
            <p className="text-xs text-gray-400 mt-1">Add documents to your session to see them here</p>
          </div>
        ) : (
          <div className="space-y-1">
            {activeDocuments.map(doc => (
              <div key={doc.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 group">
                {doc.resource_id ? (
                  <Checkbox
                    checked={selectedDocIds.has(doc.resource_id)}
                    onCheckedChange={(checked) => {
                      setSelectedDocIds(prev => {
                        const next = new Set(prev);
                        if (checked) next.add(doc.resource_id!);
                        else next.delete(doc.resource_id!);
                        return next;
                      });
                    }}
                  />
                ) : (
                  <div className="w-4" />
                )}
                <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{doc.title}</div>
                  <div className="text-xs text-gray-500">{doc.subtitle || doc.type}</div>
                </div>
                {doc.resource_id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => toggleDocInCollection(
                      // Find first collection containing this doc, or do nothing (handled via bulk picker)
                      '', doc.resource_id!
                    )}
                    title={isInAnyCollection(doc.resource_id) ? 'In a collection' : 'Not in a collection'}
                  >
                    {isInAnyCollection(doc.resource_id)
                      ? <BookmarkCheck className="w-3 h-3 text-blue-600" />
                      : <Bookmark className="w-3 h-3 text-gray-400" />}
                  </Button>
                )}
                {onRemoveActiveDocument && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => {
                      const identifier = (doc.type === 'Draft' || doc.type === 'Prosecution Doc') ? doc.resource_id : doc.title;
                      onRemoveActiveDocument(identifier!, doc.type);
                    }}
                  >
                    <X className="w-3 h-3 text-gray-500 hover:text-red-600" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex justify-between gap-2 pt-2 border-t flex-shrink-0">
        {onAddDocuments && (
          <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); onAddDocuments(); }}>
            <Plus className="w-4 h-4 mr-1" /> Add Documents
          </Button>
        )}
        <Button
          size="sm"
          disabled={selectedDocIds.size === 0}
          onClick={() => setShowBulkCollectionPicker(true)}
        >
          <FolderPlus className="w-4 h-4 mr-1" /> Add to Collection ({selectedDocIds.size})
        </Button>
      </div>
    </>
  ) : null;

  // ── Determine effective default tab ────────────────────────────────────────

  const effectiveDefault = hasActiveTab ? defaultTab : 'collections';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Documents &amp; Collections
            </DialogTitle>
            <DialogDescription>Manage active documents and collections</DialogDescription>
          </DialogHeader>

          {hasActiveTab ? (
            <Tabs defaultValue={effectiveDefault} className="flex-1 flex flex-col min-h-0">
              <TabsList className="w-full">
                <TabsTrigger value="active" className="flex-1 gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  Active Docs
                  {activeDocuments && activeDocuments.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-1">
                      {activeDocuments.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="collections" className="flex-1 gap-1.5">
                  <Bookmark className="w-3.5 h-3.5" />
                  Collections
                  {collections.length > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-1">
                      {collections.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active" className="flex-1 flex flex-col min-h-0 mt-0">
                {activeDocsContent}
              </TabsContent>

              <TabsContent value="collections" className="flex-1 flex flex-col min-h-0 mt-0">
                {collectionsContent}
              </TabsContent>
            </Tabs>
          ) : (
            // No active docs tab (e.g. DocumentsPage) — show collections only
            <div className="flex-1 flex flex-col min-h-0">
              {collectionsContent}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation sub-dialog */}
      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Collection</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{collections.find(c => c.collection_id === deleteConfirmId)?.name}&rdquo;? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk collection picker sub-dialog */}
      <Dialog open={showBulkCollectionPicker} onOpenChange={setShowBulkCollectionPicker}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add to Collection</DialogTitle>
            <DialogDescription>
              Add {selectedDocIds.size} document{selectedDocIds.size !== 1 ? 's' : ''} to a collection.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {collections.map(col => (
              <div
                key={col.collection_id}
                className="flex items-center justify-between p-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 hover:border-blue-300"
                onClick={async () => {
                  await addMultipleDocsToCollection(col.collection_id, [...selectedDocIds]);
                  setShowBulkCollectionPicker(false);
                  setSelectedDocIds(new Set());
                }}
              >
                <span className="text-sm font-medium">{col.name}</span>
                <span className="text-xs text-gray-500">{col.resource_ids.length} docs</span>
              </div>
            ))}
            {collections.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No collections yet. Create one below.</p>
            )}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t">
            <Input
              placeholder="New collection name..."
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && newCollectionName.trim()) {
                  const id = await handleCreate();
                  if (id) {
                    await addMultipleDocsToCollection(id, [...selectedDocIds]);
                    setShowBulkCollectionPicker(false);
                    setSelectedDocIds(new Set());
                  }
                }
              }}
            />
            <Button
              size="sm"
              disabled={!newCollectionName.trim()}
              onClick={async () => {
                if (newCollectionName.trim()) {
                  const id = await handleCreate();
                  if (id) {
                    await addMultipleDocsToCollection(id, [...selectedDocIds]);
                    setShowBulkCollectionPicker(false);
                    setSelectedDocIds(new Set());
                  }
                }
              }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkCollectionPicker(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
